import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";

const PAID_PLANS = new Set(["regular", "pro", "elite", "franchise"]);

export default function ProtectedRoute({ children, requireFeature, requireOwner }) {
  const { user, loading, isLoggingOut } = useAuth();
  const loc = useLocation();

  if (loading || isLoggingOut) {
    return (
      <div
        data-testid="auth-verifying"
        className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-500 font-mono text-[12px]"
      >
        {isLoggingOut ? "SIGNING_OUT…" : "VERIFYING_SESSION…"}
      </div>
    );
  }

  // Not authenticated → /login
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  }

  const role = user.role;
  const isStaff = role === "admin" || role === "owner" || role === "partner";

  // Trial / paid gating — staff always bypass.
  if (!isStaff) {
    const planId = user?.account?.plan?.plan_id || user?.plan || "free";
    const isPaid = PAID_PLANS.has(planId);
    const trialActive = !!user.trial_active;
    // Expired trial AND free plan → must subscribe.
    if (!isPaid && !trialActive) {
      return <Navigate to="/pricing?reason=trial_expired" replace />;
    }
  }

  // Feature gate (only enforced for non-staff). Trial users get full
  // feature access (mirrors backend require_feature: admin/owner OR
  // trial_active OR plan-has-feature).
  if (requireFeature && !isStaff) {
    const trialActive = !!user.trial_active;
    const features = user?.account?.plan?.features || [];
    if (!trialActive && !features.includes(requireFeature)) {
      return <Navigate to={`/account?need=${requireFeature}`} replace />;
    }
  }
  return children;
}
